import React from "react";
import { BrandAsset } from "./BrandAsset";

/**
 * RoseRow — D39 (Vania 20 Sep) + D40 (ScoreBadge) · Wave C.21 (Vania 7 Oct)
 *                                                  · Wave C.36/37 (Vania 8 Oct).
 *
 * Only roses that have been AWARDED are drawn. No empty slot. No faded-grey
 * placeholder rose. No keyline. No outline-empty. Below 3.0 the row renders
 * NOTHING (consumer surfaces must call <ScoreBadge> which already enforces
 * the threshold · we keep the same guard here so a direct RoseRow caller
 * can't bypass it either).
 *
 * Rules that must not be broken:
 *   - Roses, NEVER stars.
 *   - Every drawn rose is a delivered SVG via <BrandAsset>. NEVER a CSS-mask,
 *     redraw, recolour, rotate, or an outline silhouette for a missing one.
 *   - Half = the delivered rose_half_{colourway}.svg (ONE file, LEFT half
 *     only · W-C.39 · no grey right half anywhere).
 *   - D35 roses mapping: roundHalfDown((score − 3.0) × 2 + 1, 0.5), ties
 *     down. 4.3 → 3.5 roses, 4.0 → 3.0, 4.8 → 4.5, 3.0 → 1.0, 2.9 → no row.
 *     The demo path uses the SAME lookup as the real app · never a hand-set
 *     rose value.
 *   - The score NUMBER (D37) is Poppins 800, #FFFFFF, inside an ink #1A1A30
 *     pill, follows the roses, never leads, never Kili Yellow.
 *   - Rose height is 0.87 × the number height (W-C.36 lockup).
 *
 * SCORE PLACEMENT RULE: nothing may display a real score right now. Callers
 * must pass demo={true} for the NUMBER to render. The row of roses itself
 * still renders at value >= 3.0 (the whole point of the review-card roses).
 */
function roundHalfDown(v, step) {
  // brief: 4.25 -> 4.2 print / 4 roses; 4.26 -> 4.3 print / 4.5 roses.
  return Math.ceil(v / step - 0.5) * step;
}

// D35 (Vania · W-C.37) · map the 0..5 score to 0..5 roses in 0.5 steps.
// Below the 3.0 floor the row renders nothing. 5.0 is the ceiling.
export function rosesFromScore(score) {
  if (typeof score !== "number" || Number.isNaN(score)) return null;
  if (score < 3.0) return null;
  const raw = (score - 3.0) * 2 + 1;
  const stepped = roundHalfDown(raw, 0.5);
  return Math.max(0, Math.min(5, stepped));
}

const SCORE_FLOOR = 3.0; // D39 · below 3.0 the row is NOT drawn at all.

export function RoseRow({
  value,                 // 0..5 in 0.1 steps
  colourway = "yellow", // yellow | ink | white | cream
  size = 20,             // px per rose
  ground = "night",      // night | cream (informational only · we never draw a grey slot)
  demo = false,          // true iff caller explicitly labelled this as demo
  provisional = false,   // true iff staff desk showing a PROVISIONAL — NOT PUBLISHED number
  showNumber = true,
  showRoses = true,      // W-C.40/41 · number-only (slide 4) + roses-only (slide 5)
  testId,
}) {
  const displayValue = typeof value === "number" ? roundHalfDown(value, 0.1) : null;
  const roseValue = rosesFromScore(displayValue);
  const mayRenderNumber = demo || provisional;

  // D39 · nothing below 3.0 renders a row. ScoreBadge enforces this too, but
  // the guard lives here so a direct RoseRow caller can't bypass D39.
  if (displayValue == null || displayValue < SCORE_FLOOR || roseValue == null) {
    return null;
  }

  // Build the roses list from the D35 mapping · no trailing empties.
  const slots = [];
  const fulls = Math.floor(roseValue);
  const hasHalf = roseValue - fulls >= 0.5;
  for (let i = 0; i < fulls; i++) slots.push(`rose.full.${colourway}`);
  if (hasHalf) slots.push(`rose.half.${colourway}`);

  // W-C.36 lockup geometry · roses are 0.87 × the number height so the
  // number is the reference line. Caller passes `size` meaning rose px.
  const numberHeight = Math.round(size / 0.87);
  const badgePadY = Math.max(2, Math.round(numberHeight * 0.14));
  const badgePadX = Math.max(6, Math.round(numberHeight * 0.42));
  const numberFont = Math.round(numberHeight * 0.72);

  return (
    <span
      role={mayRenderNumber ? "img" : "presentation"}
      aria-label={
        provisional ? `provisional score ${displayValue}` :
        demo ? `demo score ${displayValue}` :
        "score"
      }
      data-testid={testId || "rose-row"}
      data-demo={demo ? "true" : "false"}
      data-provisional={provisional ? "true" : "false"}
      data-ground={ground}
      className="inline-flex items-center gap-1"
      style={{ display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      <span style={{ display: "inline-flex", alignItems: "center", gap: 2 }}>
        {showRoses && slots.map((slot, i) => (
          <BrandAsset key={i} slot={slot} width={size} height={size} alt="" />
        ))}
      </span>
      {showNumber && mayRenderNumber && displayValue != null && (
        <span
          data-testid="score-number"
          data-score-badge="ink"
          style={{
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: "#1A1A30",
            color: "#FFFFFF",         // NEVER Kili Yellow
            fontFamily: `Poppins, "Helvetica Neue", Helvetica, Arial, sans-serif`,
            fontWeight: 800,
            fontSize: numberFont,
            lineHeight: 1,
            padding: `${badgePadY}px ${badgePadX}px`,
            borderRadius: 999,
            fontVariantNumeric: "tabular-nums",
            letterSpacing: "-0.01em",
            minHeight: numberHeight,
          }}
        >{displayValue.toFixed(1)}</span>
      )}
    </span>
  );
}

export default RoseRow;
